/**
 * Centralized print utility for SMARTIS
 * Provides consistent print formatting across all pages
 */

const SCHOOL_COLORS = {
  primary: "#00305E",
  accent: "#A62E1D",
  lightGray: "#f5f5f5",
  medGray: "#666",
  border: "#ddd",
};

const BASE_STYLES = `
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #1a1a1a;
    line-height: 1.5;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 16px; color: ${SCHOOL_COLORS.primary}; margin: 18px 0 8px; }
  h3 { font-size: 14px; color: #333; margin: 12px 0 6px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
  th, td { border: 1px solid ${SCHOOL_COLORS.border}; padding: 6px 8px; text-align: left; }
  th { background: ${SCHOOL_COLORS.lightGray}; font-weight: 600; font-size: 11px; }
  .print-header {
    text-align: center;
    border-bottom: 2px solid ${SCHOOL_COLORS.primary};
    padding-bottom: 14px;
    margin-bottom: 20px;
  }
  .print-header h1 { color: ${SCHOOL_COLORS.primary}; }
  .print-header .subtitle { color: ${SCHOOL_COLORS.medGray}; font-size: 12px; margin-top: 2px; }
  .print-timestamp { color: ${SCHOOL_COLORS.medGray}; font-size: 11px; margin-bottom: 16px; }
  .print-footer {
    margin-top: 30px;
    padding-top: 12px;
    border-top: 1px solid ${SCHOOL_COLORS.border};
    font-size: 11px;
    color: ${SCHOOL_COLORS.medGray};
  }
  .section { margin-bottom: 16px; }
  .section-title {
    font-weight: 600;
    color: #333;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
  .section-content {
    background: #f9fafb;
    padding: 10px 12px;
    border-radius: 4px;
    border-left: 3px solid ${SCHOOL_COLORS.primary};
    font-size: 13px;
  }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
  .field-label { font-size: 11px; color: ${SCHOOL_COLORS.medGray}; margin-bottom: 2px; }
  .field-value { font-weight: 500; font-size: 13px; }
  .summary-box {
    display: inline-block;
    padding: 8px 16px;
    margin: 4px;
    border: 1px solid ${SCHOOL_COLORS.border};
    border-radius: 6px;
    text-align: center;
  }
  .summary-value { font-size: 22px; font-weight: bold; }
  .summary-label { font-size: 11px; color: ${SCHOOL_COLORS.medGray}; }
  .badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }
  .no-print { display: none !important; }
  @media print {
    body { padding: 10px; }
    @page { margin: 1cm; }
  }
`;

/**
 * Open a new print window with standardized layout
 *
 * @param {Object} options
 * @param {string} options.title - Document title / heading
 * @param {string} options.content - Inner HTML body content
 * @param {string} [options.subtitle] - Optional subtitle under the title
 * @param {string} [options.extraStyles] - Additional CSS appended after base styles
 * @param {string} [options.accentColor] - Override primary accent (e.g. for staff reports)
 * @param {boolean} [options.landscape] - Use landscape orientation
 * @param {boolean} [options.showFooter=true] - Show/hide footer
 * @param {string} [options.bodyClass] - Optional class on <body>
 * @param {number} [options.printDelay=100] - ms delay before triggering print dialog
 */
export function printPage({
  title,
  content,
  subtitle = "SMARTIS",
  extraStyles = "",
  accentColor,
  landscape = false,
  showFooter = true,
  bodyClass = "",
  printDelay = 100,
}) {
  const accent = accentColor || SCHOOL_COLORS.primary;
  const orientationCSS = landscape
    ? `@page { size: landscape; margin: 0.5cm; }`
    : "";

  const accentOverride = accentColor
    ? `
      .print-header { border-bottom-color: ${accent}; }
      .print-header h1 { color: ${accent}; }
      .section-content { border-left-color: ${accent}; }
    `
    : "";

  const footerHTML = showFooter
    ? `<div class="print-footer">
        <p>Generated by SMARTIS &mdash; ${new Date().toLocaleString()}</p>
      </div>`
    : "";

  const w = window.open("", "_blank");
  if (!w) return;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${title} - SMARTIS</title>
  <style>
    ${BASE_STYLES}
    ${accentOverride}
    ${orientationCSS}
    ${extraStyles}
  </style>
</head>
<body class="${bodyClass}">
  <div class="print-header">
    <h1>${title}</h1>
    ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ""}
  </div>
  <div class="print-timestamp">Generated: ${new Date().toLocaleString()}</div>
  ${content}
  ${footerHTML}
</body>
</html>`);
  w.document.close();
  setTimeout(() => w.print(), printDelay);
}

/**
 * Print the innerHTML of a ref element with standardized layout.
 * Drop-in replacement for the old ReportsPage handlePrint pattern.
 */
export function printRef(title, contentRef, options = {}) {
  printPage({
    title,
    content: contentRef.current?.innerHTML || "",
    ...options,
  });
}
